import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import PropTypes from "prop-types";

import LabTestCatalogService from "./LabTestCatalogService";
import LabInvestigationService from "./LabInvestigationService";
import DigitisationService from "./DigitisationService";
import { fetchUnitOptions, getCachedUnitOptions } from "./LabUnitsService";
import { fetchSubtestTags, getTagsForSltCode } from "./SubtestTagsService";
import { SENTINEL_DOC_IDS, isSentinelDocId } from "./DigitiseModal";
import EventService from "service/event/EventService";
import { LI_V2_REFRESH } from "./events";
import { useCardDraft } from "../common/useCardDraft";
import {
  TestCard,
  PlusIcon,
  blankTest,
  blankSubTest,
  nextId,
  todayISO,
} from "./DigitiseManuallyParts";
import {
  setCombo,
  clearCombo,
  isDuplicate,
  subscribe as subscribeCombos,
} from "./duplicateGuard";

/**
 * DigitiseManuallyCard — the card-shaped Digitise-manually surface used
 * by the standalone rail sheet AND the transcript-mode review flow.
 * Rendered inline as a right-side sheet (no ModalPortal, no backdrop, no
 * outcome overlays) and scoped to ONE parent test with N sub-tests.
 *
 * Standalone path: card owns the save round-trip via
 * `LabInvestigationService.batch`, seeded from the DigitiseModal doc
 * chooser (`__digitiseManual` LI_V2_EDIT payload).
 * Transcript path: card hands every commit back to its parent via
 * `onAdd` / `onAddAnother` so the transcript flow can reconcile against
 * captured items and dispatch the batch at Save & next.
 * Shortcut path (standalone-only): reviewer clicked a patient row in
 * the section table. Card is pre-seeded with that Investigation's
 * parent lab test + sub-test + LOINC + last unit; Document ID becomes
 * an editable dropdown the card populates itself via DigitisationService.
 * Save appends a new reading to the SAME Investigation via the batch
 * endpoint's append-only dedup path (no need for the reviewer to re-
 * pick the catalog test).
 *
 * One-card-per-(Lab test, Test date) rule:
 *   • "+ Add sub test" appends a sub-test row inside this card.
 *   • "+ Add another test" commits the current card AND signals the
 *     parent to spawn a fresh empty card (a different (test, date)
 *     combination). The commit path is the same as clicking Add.
 *
 * Doc ID is pinned to `user-informed-verbally` in transcript mode — the
 * reviewer isn't reading from a document during the call. The
 * "View document" affordance is hidden for the same reason (nothing to
 * open on a verbal capture).
 */

const USER_INFORMED_VERBALLY = "user-informed-verbally";

// Doc-row label — resolve either a sentinel (User informed verbally,
// User informed via app) or a real document ID against the supplied
// documents list.
const displayDocLabel = (docId, documents) => {
  const s = String(docId || "");
  const sentinel = SENTINEL_DOC_IDS.find((x) => x.value === s);
  if (sentinel) return sentinel.label;
  const doc = (documents || []).find((d) => String(d.id) === s);
  if (doc) return `${doc.id}${doc.name ? ` — ${doc.name}` : ""}`;
  return s || "—";
};

const seedTestFromEditing = (editing) => {
  if (!editing) return blankTest();
  const subTests = Array.isArray(editing.subTests) && editing.subTests.length > 0
    ? editing.subTests.map((s) => ({
        id:           s.id || nextId(),
        sub:          s.sub || null,
        loincName:    s.loincName || "",
        loincLoading: false,
        value:        s.value || "",
        unit:         s.unit || "",
        range:        s.range || "",
        tag:          s.tag || "",
      }))
    : [blankSubTest()];
  return {
    id:        editing.id || nextId(),
    collapsed: false,
    labTest:   editing.labTest || null,
    testDate:  editing.testDate || "",
    subTests,
  };
};

// Shortcut mode — reviewer clicked an existing sub-test row in the LI
// section table to add a new reading. Seed the parent lab test stub +
// the first sub-test row from the row's `test` snapshot; catalog
// reconciliation (LOINC-first) on mount swaps both with the canonical
// catalog objects so the pickers render correctly. Unit is pre-filled
// from the row (rarely changes reading-to-reading); Value / Range /
// Tag / Test date are LEFT BLANK for the reviewer to enter.
const seedTestFromShortcut = (investigation) => {
  const inv = investigation || {};
  const t = inv.test || {};
  const stubParent = {
    testName:  t.panel || t.name || "",
    ltCode:    t.ltCode || "",
    loincCode: t.panel ? "" : (t.loincCode || ""),
    // Stub the subtest[] with THIS row's sub only — the catalog effect
    // replaces the parent with the real record on mount, at which
    // point every option upstream appears in the dropdown.
    subtest:   t.sltCode || t.name
      ? [{
          sltCode:     t.sltCode || "",
          subtestName: t.name || "",
          loincCode:   t.loincCode || "",
        }]
      : null,
  };
  const stubSub = (t.sltCode || t.name)
    ? {
        sltCode:     t.sltCode || "",
        subtestName: t.name || "",
        loincCode:   t.loincCode || "",
      }
    : null;
  return {
    id:        nextId(),
    collapsed: false,
    labTest:   stubParent,
    testDate:  "",
    subTests: [{
      id:           nextId(),
      sub:          stubSub,
      // Leave blank — the catalog-reconciliation + loinc-backfill effect
      // in DigitiseManuallyCard will fetch the proper LOINC name once the
      // catalog resolves. Seeding with t.loincCode here would masquerade
      // the code (e.g. "40754-4") as the name in the input until the
      // reviewer re-selects the Sub test.
      loincName:    "",
      loincLoading: false,
      value:        "",
      unit:         t.unit || "",
      range:        "",
      tag:          "",
      description:  "",
    }],
  };
};

const DigitiseManuallyCard = ({
  formId,
  editing,
  onAdd,
  onAddAnother,
  onClose,
  onReject,
  saving,
  error,
  themeClass,
  blockedBySibling,
  // Sheet-stack blank-guard hook. Optional — absent on standalone. The
  // transcript rail passes it so its shared cardFilledRef learns the
  // card is no longer "blank" as soon as the reviewer picks a lab
  // test, and a "+ Add investigation" click can't stack another empty
  // sheet on top.
  onFilledChange,
  // Standalone-mode extras. When these are supplied the card owns
  // the save round-trip (LabInvestigationService.batch) instead of
  // handing the commit back to a transcript-side parent via onAdd.
  patientId,
  appointmentId,
  docId,
  documents,
  docToken,
  docVersion,
  onSaved,
  // Shortcut mode — reviewer clicked an existing patient row to add
  // a new reading against the SAME test. Card seeds the parent lab
  // test + sub-test + unit from this Investigation, then behaves as a
  // standalone card (patientId + docVersion still required) except
  // the Document ID row is an editable dropdown the card populates
  // itself from DigitisationService.
  shortcutFrom,
}) => {
  const isShortcut = !!shortcutFrom;
  const isUpdate = !!editing;

  // ZY-10356 Phase 1 — transcript-mode draft preservation. No-op in
  // standalone. `test` is a single bag holding labTest + subTests[] so
  // one snapshot persists the whole form.
  const [draft, saveDraft] = useCardDraft(formId);

  const [test, setTest] = useState(() => {
    if (draft && "test" in draft) return draft.test;
    return isShortcut ? seedTestFromShortcut(shortcutFrom) : seedTestFromEditing(editing);
  });
  useEffect(() => { saveDraft({ test }); }, [saveDraft, test]);
  const [feedback, setFeedback] = useState(null);
  // Standalone-only save spinner. Kept separate from `saving` (the
  // transcript-flow prop) so the two paths don't step on each other.
  const [standaloneSaving, setStandaloneSaving] = useState(false);

  // Shortcut-mode doc chooser. Card self-fetches the document list
  // (same DigitisationService.listDocuments the Digitise chooser uses)
  // and manages `shortcutDocId` locally so the reviewer picks doc-or-
  // sentinel inside the sheet. Non-shortcut paths keep the prop-driven
  // `docId` from the parent DigitiseModal chooser.
  const [shortcutDocId, setShortcutDocId] = useState("");
  const [shortcutDocs, setShortcutDocs] = useState([]);
  const [shortcutDocToken, setShortcutDocToken] = useState("");
  const [shortcutDocsLoading, setShortcutDocsLoading] = useState(isShortcut);
  useEffect(() => {
    if (!isShortcut || !(Number.isFinite(patientId) && patientId > 0)) return;
    const svc = new DigitisationService();
    let alive = true;
    svc.listDocuments(
      patientId,
      (data) => {
        if (!alive) return;
        setShortcutDocs(Array.isArray(data) ? data : []);
        setShortcutDocsLoading(false);
      },
      () => {
        if (!alive) return;
        setShortcutDocs([]);
        setShortcutDocsLoading(false);
      },
    );
    svc.getDocumentToken(
      (t) => { if (alive) setShortcutDocToken(t || ""); },
      () => {},
    );
    return () => { alive = false; };
  }, [isShortcut, patientId]);

  // Doc-row resolution.
  //   • Shortcut mode: the reviewer's in-sheet pick (`shortcutDocId`)
  //     drives everything — Save is gated on it being non-empty.
  //   • Transcript mode: `docId` is unset and the "User informed
  //     verbally" sentinel ships.
  //   • Standalone (from DigitiseModal): the modal supplies the real
  //     document id.
  const effectiveDocId = isShortcut
    ? shortcutDocId
    : (docId || USER_INFORMED_VERBALLY);
  const effectiveDocs = isShortcut ? shortcutDocs : (documents || []);
  const effectiveDocToken = isShortcut ? shortcutDocToken : docToken;
  const isSentinel = isSentinelDocId(effectiveDocId);
  const docObj = !isSentinel
    ? effectiveDocs.find((d) => String(d.id) === String(effectiveDocId))
    : null;
  const canViewDoc = !!(docObj && docObj.url);
  const isStandalone = Number.isFinite(patientId) && patientId > 0
    && typeof docVersion === "number";

  // ═════════════════════════════════════════════════════════════════
  //  LOCKED — Transcript-mode blank-guard hook (2026-09-21)
  // ═════════════════════════════════════════════════════════════════
  //  Report "not blank" up to the shared cardFilledRef the transcript
  //  rail's blank-guard reads (CapturedSections.js). A card is filled
  //  the moment the reviewer picks a parent lab test — matching how
  //  CC / Dx forms decide filled as soon as their name field is
  //  non-empty. Kept even in shortcut / update mode (which pre-seed a
  //  labTest) so those are considered filled on mount and don't trip
  //  the guard.
  //
  //  Removing this effect (or narrowing the `test.labTest` predicate)
  //  will silently break the "no second blank card" rule in transcript
  //  mode — DigitiseManuallyCard would look permanently blank to the
  //  guard and clicks on "+ Add investigation" / "+ Add another test"
  //  would either always refuse (guard on) or freely stack empty cards
  //  (guard off). Both regressed once already. Do NOT weaken silently.
  //  See feedback_transcript_blank_guard_locked.md.
  useEffect(() => {
    if (typeof onFilledChange !== "function" || formId == null) return;
    onFilledChange(formId, !!(test && test.labTest));
  }, [formId, onFilledChange, test.labTest]);

  // Cross-card duplicate coordination — see duplicateGuard.js. On
  // (labTest, testDate) change we register this card's current combo
  // into the shared map; on unmount we clear it. Any card can query
  // whether its own combo collides with ANOTHER card's; the mini
  // subscription bus forces a re-render when the shared map changes
  // so the isDup gate below stays live even when a sibling card
  // moves.
  const ltCode = test.labTest && test.labTest.ltCode;
  const combiDate = test.testDate;
  const cardKey = formId != null ? String(formId) : "";
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribeCombos(forceUpdate), []);
  useEffect(() => {
    if (!cardKey) return undefined;
    setCombo(cardKey, ltCode, combiDate);
    return () => clearCombo(cardKey);
  }, [cardKey, ltCode, combiDate]);
  const isDup = cardKey ? isDuplicate(cardKey, ltCode, combiDate) : false;

  // Catalog corpus for the pickers — loaded ONCE on mount. fetchAll
  // gives {ltCode, testName, loincCode, subtest:[{sltCode,
  // subtestName, ...}]}.
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [unitOptions, setUnitOptions] = useState(() => getCachedUnitOptions() || []);

  useEffect(() => {
    const svc = new LabTestCatalogService();
    svc.getAllWithSubTests(
      (list) => {
        setCatalog(list);
        setCatalogLoading(false);
        // Reconcile the seeded labTest (built from captured payloads
        // with only a stub corpus) against the just-loaded catalog so
        // the sub-test picker's dropdown carries every option, not
        // just the current picks. Preserves the reviewer's in-flight
        // state on `subTests[]` — only the parent lab test + each
        // row's `sub` reference get swapped with catalog canonicals.
        //
        // Identity resolution — LOINC first, then ltCode, then name:
        //   1. LOINC-first — the transcript agent's server-side LOINC
        //      resolution IS the identity we want. Build a one-shot
        //      reverse index `loincCode → {parent, sub}` across every
        //      catalog entry's subtest[] and pick the first row whose
        //      loinc hits it. Beats name-match because the LLM's
        //      canonical panel name ("CBC") often diverges from the
        //      catalog's ("Complete Blood Count (CBC)"). First-hit-
        //      wins on duplicate LOINCs — rare (some LOINCs appear in
        //      multiple panels) and the reviewer can swap by hand.
        //   2. ltCode — set on manual-card re-edits (LabInvestigation-
        //      Form update flow) via parent_lt_code passthrough.
        //   3. testName lowercased — final fallback for old rows
        //      without either.
        const loincIndex = new Map();
        list.forEach((parent) => {
          (parent.subtest || []).forEach((s) => {
            const code = String(s.loincCode || "").trim();
            if (code && !loincIndex.has(code)) {
              loincIndex.set(code, { parent, sub: s });
            }
          });
        });
        setTest((prev) => {
          if (!prev.labTest) return prev;

          let resolvedParent = null;
          for (let i = 0; i < prev.subTests.length; i += 1) {
            const row = prev.subTests[i];
            const code = row.sub && String(row.sub.loincCode || "").trim();
            if (!code) continue;
            const hit = loincIndex.get(code);
            if (hit) { resolvedParent = hit.parent; break; }
          }
          if (!resolvedParent && prev.labTest.ltCode) {
            resolvedParent = list.find((t) => t.ltCode === prev.labTest.ltCode) || null;
          }
          if (!resolvedParent) {
            const byName = (prev.labTest.testName || "").toLowerCase();
            resolvedParent = list.find((t) => (t.testName || "").toLowerCase() === byName) || null;
          }
          if (!resolvedParent) return prev;

          // Reconcile every sub-test row against the resolved parent's
          // catalog subtest[] so SearchSelect's selected state renders
          // and `sub.sltCode` populates the identity gate at save time.
          // Match order per row: LOINC → sltCode → name.
          const parentSubs = Array.isArray(resolvedParent.subtest) ? resolvedParent.subtest : [];
          const rowsResolved = prev.subTests.map((row) => {
            if (!row.sub) return row;
            const rowLoinc = String(row.sub.loincCode || "").trim();
            const rowSlt = String(row.sub.sltCode || "").trim();
            const rowName = String(row.sub.subtestName || "").toLowerCase();
            const match =
              (rowLoinc && parentSubs.find((s) => String(s.loincCode || "").trim() === rowLoinc)) ||
              (rowSlt && parentSubs.find((s) => s.sltCode === rowSlt)) ||
              parentSubs.find((s) => (s.subtestName || "").toLowerCase() === rowName);
            if (!match) return row;
            return { ...row, sub: match };
          });

          return { ...prev, labTest: resolvedParent, subTests: rowsResolved };
        });
      },
      (err) => {
        setCatalog([]);
        setCatalogLoading(false);
        setCatalogError((err && err.message) || "Couldn't load lab test list.");
      },
    );
    fetchUnitOptions().then((rows) => {
      if (rows && rows.length) setUnitOptions(rows);
    });
    // Prime the SLT-code → tag catalog so the SubTestCard's Tag
    // dropdown appears without a follow-up round trip.
    fetchSubtestTags();
  }, []);

  // Card is single-test, so pickedLtCodes only ever holds this card's
  // own choice (feeds TestCard's own-vs-others filter — a no-op here
  // but the prop is required).
  const pickedLtCodes = useMemo(() => {
    const s = new Set();
    if (test.labTest && test.labTest.ltCode) s.add(test.labTest.ltCode);
    return s;
  }, [test.labTest]);

  const updateTest = (patch) => setTest((prev) => ({ ...prev, ...patch }));
  const pickLabTest = (labTest) => {
    // Changing the parent test wipes existing sub-test picks — they
    // belong to a different test's subtest[] and shouldn't linger.
    // When the parent has exactly ONE sub-test, auto-pick it: the
    // reviewer would otherwise have to open a picker with a single
    // option in it. Fires the standard loinc fetch on the same code
    // path pickSubTest would.
    const corpus = Array.isArray(labTest && labTest.subtest) ? labTest.subtest : [];
    const auto = corpus.length === 1 ? corpus[0] : null;
    const seed = blankSubTest();
    const seededRow = auto
      ? { ...seed, sub: auto, loincName: "", loincLoading: !!(labTest && labTest.ltCode) }
      : seed;
    setTest((prev) => ({ ...prev, labTest, subTests: [seededRow] }));
    if (auto && labTest && labTest.ltCode) {
      const rowId = seededRow.id;
      new LabTestCatalogService().getSubTestOnSelect(
        labTest.ltCode,
        auto.sltCode,
        (res) => updateSubTest(rowId, {
          loincName: (res && res.loincName) || "",
          loincLoading: false,
        }),
        () => updateSubTest(rowId, { loincLoading: false }),
      );
    }
  };
  const addSubTest = () => {
    setTest((prev) => ({ ...prev, subTests: prev.subTests.concat(blankSubTest()) }));
  };
  const removeSubTest = (subId) => {
    setTest((prev) => ({
      ...prev,
      subTests: prev.subTests.filter((s) => s.id !== subId),
    }));
  };
  const updateSubTest = (subId, patch) => {
    setTest((prev) => ({
      ...prev,
      subTests: prev.subTests.map((s) => (s.id === subId ? { ...s, ...patch } : s)),
    }));
  };

  // Backfill `loincName` on reopen. Pick / Auto-pick paths both fire
  // getSubTestOnSelect inline, but a card seeded from an existing row
  // (shortcut mode, transcript re-edit, standalone update) arrives with
  // the sub pre-picked and no name attached — the previous flow then
  // rendered the fallback `sub.loincCode` ("40754-4") in the LOINC name
  // field, and the only way to populate the real name was to re-select
  // the Sub test. This effect walks the sub-test rows after the catalog
  // has resolved and fires getSubTestOnSelect for any row that has a
  // sltCode + a resolved parent ltCode + no name yet.
  //
  // ONE ATTEMPT PER ROW + SUB TEST, kept for the card's lifetime. The key
  // goes in before the fetch — setting `loincLoading: true` via setState is
  // async, so a dependency-driven effect would otherwise re-fire it in the
  // gap — and it STAYS in when the answer lands. It used to be deleted then,
  // and a sub-test the catalogue has no LOINC name for answers "": the name
  // stayed empty, the effect re-ran on that very update, and the card
  // re-fetched in a loop (~700 requests a second, measured) while the churn
  // made its fields un-typeable (2026-10-09). A different Sub test is a new
  // key, and picking one fetches inline anyway.
  const triedLoincRef = useRef(new Set());
  useEffect(() => {
    if (catalogLoading) return;
    const ltCode = test.labTest && test.labTest.ltCode;
    if (!ltCode) return;
    const svc = new LabTestCatalogService();
    test.subTests.forEach((row) => {
      const sltCode = row.sub && row.sub.sltCode;
      if (!sltCode) return;
      if (row.loincLoading) return;
      if (row.loincName) return;
      const key = `${row.id}|${ltCode}|${sltCode}`;
      if (triedLoincRef.current.has(key)) return;
      triedLoincRef.current.add(key);
      const rowId = row.id;
      updateSubTest(rowId, { loincLoading: true });
      svc.getSubTestOnSelect(
        ltCode,
        sltCode,
        (res) => {
          updateSubTest(rowId, {
            loincName: (res && res.loincName) || "",
            loincLoading: false,
          });
        },
        () => {
          updateSubTest(rowId, { loincLoading: false });
        },
      );
    });
  }, [catalogLoading, test.labTest, test.subTests]);

  // Every rendered field must be filled before Add is enabled. Scoped
  // to ONE test. Tag becomes required whenever the picked sub-test's
  // sltCode has a curated tag list.
  //
  // ZY-10340 change: at LEAST one of {value, description} is required
  // (was: value required). Unit and Range stay strictly required so
  // the trend chart still has an axis to plot on — a description-only
  // row is a note against the sub-test, not a plottable reading, but
  // the reviewer still picks a unit + range for consistency with the
  // rest of the audit history.
  const isSubRowComplete = (s, hasCorpus) => {
    if (hasCorpus && !s.sub) return false;
    const hasValue = !!String(s.value).trim();
    const hasDesc = !!String(s.description || "").trim();
    if (!hasValue && !hasDesc) return false;
    if (!s.unit) return false;
    if (!s.range) return false;
    const sltCode = s.sub && s.sub.sltCode;
    if (sltCode && getTagsForSltCode(sltCode).length > 0 && !s.tag) return false;
    return true;
  };
  const isTestComplete = (t) => {
    if (!t.labTest) return false;
    if (!t.testDate) return false;
    if (t.testDate > todayISO()) return false;
    const hasCorpus = Array.isArray(t.labTest.subtest) && t.labTest.subtest.length > 0;
    if (t.subTests.length === 0) return false;
    return t.subTests.every((s) => isSubRowComplete(s, hasCorpus));
  };
  const isBusy = !!saving || standaloneSaving;
  const shortcutDocReady = !isShortcut || !!shortcutDocId;
  const canSubmit = isTestComplete(test) && shortcutDocReady && !isBusy && !blockedBySibling && !isDup;
  const submitHint = !test.labTest
    ? "Select a lab test to continue"
    : !test.testDate
    ? "Pick the test date"
    : isShortcut && !shortcutDocId
    ? "Pick a Document ID first"
    : isDup
    ? "Another open card already covers this lab test + date"
    : !canSubmit
    ? "Fill every field before adding"
    : undefined;

  // Package the card's state into the shape the transcript adapter
  // wants — one row per sub-test, with parent lab test + testDate
  // shared. The reviewer's Add commits back through onAdd; the
  // "+ Add another test" path shortcut-commits then signals the
  // parent to spawn a fresh empty card.
  const buildCommitPayload = () => ({
    labTest:  test.labTest,
    testDate: test.testDate,
    subTests: test.subTests.map((s) => ({
      id:          s.id,
      sub:         s.sub,
      loincName:   s.loincName,
      value:       String(s.value).trim(),
      unit:        s.unit,
      range:       s.range,
      tag:         s.tag || "",
      description: String(s.description || "").trim(),
    })),
  });

  // Build the flat Lab v2 BatchAddRequest body from the card's local
  // state — single parent test with N sub-tests.
  const buildBatchBody = () => {
    const parent = test.labTest;
    if (!parent) return null;
    const hasCorpus = Array.isArray(parent.subtest) && parent.subtest.length > 0;
    const investigations = test.subTests.map((row) => {
      const testSnapshot = hasCorpus
        ? {
            sltCode:   (row.sub && row.sub.sltCode) || "",
            ltCode:    parent.ltCode || "",
            loincCode: (row.sub && row.sub.loincCode) || "",
            name:      (row.sub && row.sub.subtestName) || "",
            panel:     parent.testName || "",
            unit:      row.unit || "",
            refRange:  { text: "" },
            // Tag lives on TestSnapshot per backend model — every
            // StatusEntry carries its own snapshot so audit / trend
            // rows can be distinguished per reading.
            tag:       row.tag || "",
          }
        : {
            sltCode:   "",
            ltCode:    parent.ltCode || "",
            loincCode: parent.loincCode || "",
            name:      parent.testName || "",
            panel:     "",
            unit:      row.unit || "",
            refRange:  { text: "" },
            tag:       row.tag || "",
          };
      const numeric = Number(String(row.value).replace(/[^0-9.-]/g, ""));
      return {
        test: testSnapshot,
        testDate: test.testDate ? new Date(test.testDate).toISOString() : new Date().toISOString(),
        value: String(row.value).trim(),
        numericValue: isNaN(numeric) ? undefined : numeric,
        flag: row.range || "",
        description: String(row.description || "").trim(),
      };
    });
    const aptId = Number(appointmentId);
    return {
      documentId: effectiveDocId,
      appointmentId: Number.isFinite(aptId) && aptId > 0 ? aptId : 0,
      source: "manual",
      investigations,
      // Append-only write — the card's save is always a batch of
      // appends (fresh Investigations, or new StatusEntries against
      // existing tests via server-side testIdentityKey dedup). Neither
      // outcome depends on the pre-state, so strict OCC on the client
      // buys nothing. Send the -1 sentinel and let the backend run its
      // append-only 3-attempt retry loop. Same reasoning applies to
      // "+ Add investigation" via LabInvestigationForm.
      expectedVersion: -1,
    };
  };

  const viewDocument = () => {
    if (!canViewDoc) return;
    const sep = docObj.url.includes("?") ? "&" : "?";
    const url = effectiveDocToken
      ? `${docObj.url}${sep}token=${encodeURIComponent(effectiveDocToken)}`
      : docObj.url;
    window.open(url, "_blank", "noopener");
  };

  const commit = () => {
    if (!canSubmit) return;
    setFeedback(null);
    // Standalone path — card owns the save round-trip. Batch is
    // append-only and uses the -1 sentinel (see buildBatchBody), so no
    // client-side refetch-and-retry is needed; the backend absorbs
    // version drift itself.
    if (isStandalone) {
      const body = buildBatchBody();
      if (!body) return;
      setStandaloneSaving(true);
      const svc = new LabInvestigationService();
      svc.batch(
        patientId,
        body,
        () => {
          setStandaloneSaving(false);
          EventService.emit(LI_V2_REFRESH);
          if (typeof onSaved === "function") onSaved();
          else onClose();
        },
        (err) => {
          setStandaloneSaving(false);
          setFeedback({
            kind: "err",
            text: (err && err.message) || "Save failed — please retry",
          });
        },
      );
      return;
    }
    // Transcript path — hand the commit to the parent.
    onAdd(buildCommitPayload());
  };

  // "+ Add another test" — spawns a NEW empty card in the rail while
  // leaving this one open (parallel-edit UX). Doesn't commit the
  // current card. Gate only requires Lab test + Test date so the
  // reviewer can queue a sibling card as soon as those two anchors
  // are picked, even before the sub-test rows are complete.
  const canAddAnother = !!(test.labTest && test.testDate) && !isBusy && !blockedBySibling && !isDup;
  const openAnotherCard = () => {
    if (!canAddAnother) return;
    if (typeof onAddAnother !== "function") return;
    if (isShortcut) {
      // Shortcut mode manages docId + docs list + token locally; the
      // sibling card should inherit them so the reviewer doesn't re-
      // pick a document for a second reading against a different test
      // in the SAME report. Editor's onAddAnother reads this payload
      // and emits an __digitiseManual event.
      onAddAnother({
        docId:      shortcutDocId,
        documents:  shortcutDocs,
        docToken:   shortcutDocToken,
      });
    } else {
      onAddAnother();
    }
  };

  const rootClass = [
    "cc3-form",
    // Same gate as the primary button label — `.cc3-update` is the
    // standalone-edit orange chrome; in transcript mode themeClass
    // (is-ai / is-manual / is-update) already carries the right look.
    isUpdate && isStandalone ? "cc3-update" : "",
    themeClass || "",
    "li-dm-card",
  ].filter(Boolean).join(" ");

  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";

  return (
    <div className={rootClass}>
      {/* Card head — bold h2 title, no subtitle, no chevron toggle:
          the card is scoped to ONE (test, date) group so a collapse
          affordance adds no value. */}
      <div className="li-dm-card-head">
        <h2 className="li-dm-card-title">Digitise manually</h2>
      </div>

      <div className="li-dm-card-body">

        {/* Doc ID row — resolves the effective doc from `docId`. In
            transcript mode this is the "User informed verbally"
            sentinel; in standalone mode DigitiseModal passes the doc
            the reviewer just picked. In shortcut mode the reviewer
            picks in-card from a dropdown fed by DigitisationService.
            "View document" only surfaces when the pick resolved to a
            real, viewable document. */}
        <div className="li-dm-doc-row">
          <div className="li-dm-field li-dm-field-doc">
            <label className="li-dm-lbl">
              Document ID <span className="li-dm-req">*</span>
            </label>
            {isShortcut ? (
              <div className="li-dm-select-wrap">
                <select
                  className="li-dm-select"
                  value={shortcutDocId}
                  onChange={(e) => setShortcutDocId(e.target.value)}
                  disabled={isBusy || shortcutDocsLoading}
                >
                  <option value="" disabled>
                    {shortcutDocsLoading ? "Loading documents…" : "Select a document"}
                  </option>
                  {shortcutDocs.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.id}{d.name ? ` — ${d.name}` : ""}
                    </option>
                  ))}
                  {SENTINEL_DOC_IDS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <span className="li-dm-chev" aria-hidden="true">&#8964;</span>
              </div>
            ) : (
              /* Not shortcut mode → the pick already happened upstream
                 (transcript sentinel or DigitiseModal chooser). Field
                 is a read-only display; no chevron because there's no
                 dropdown to hint at. */
              <div className="li-dm-input is-readonly is-doc-readonly">
                {displayDocLabel(effectiveDocId, effectiveDocs)}
              </div>
            )}
          </div>
          {!isSentinel && effectiveDocId && (
            <button
              type="button"
              className="li-dm-btn li-dm-btn-ghost li-dm-view-doc"
              onClick={viewDocument}
              disabled={!canViewDoc || isBusy}
              title={!canViewDoc ? "This document has no viewable link" : undefined}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                <path d="M2 2h6v2H4v8h8V8h2v6H2V2z" />
                <path d="M10 2h4v4M14 2 8 8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              View document
            </button>
          )}
        </div>

        <div className="li-dm-card-rule" aria-hidden="true" />

        {catalogError && (
          <div className="li-dm-catalog-warn">{catalogError}</div>
        )}

        {/* Single shared TestCard from DigitiseManuallyParts.
            onRemove is wired to onClose so the close-X folds back into
            the Cancel-affordance the transcript card already has — no
            silent no-op, no orphan button. */}
        <TestCard
          index={1}
          test={test}
          catalog={catalog}
          catalogLoading={catalogLoading}
          unitOptions={unitOptions}
          pickedLtCodes={pickedLtCodes}
          onUpdate={updateTest}
          onPickLabTest={pickLabTest}
          onRemove={onClose}
          onAddSub={addSubTest}
          onRemoveSub={removeSubTest}
          onUpdateSub={updateSubTest}
          disabled={isBusy}
        />

        {/* "+ Add another test" — spawns a fresh empty card in the
            rail for a DIFFERENT (test, date) combination. Does NOT
            commit the current card. Enabled as soon as Lab test +
            Test date are picked so the reviewer can queue a sibling
            card early. Add (footer) remains the gatekeeper for
            full-field validation. Hover hint uses the shared white /
            black .li-dm-tt popover — no native `title` (which shows
            the browser's default grey tooltip). */}
        {(() => {
          const disabledHint = !test.labTest
            ? "Pick a lab test first"
            : !test.testDate
            ? "Pick a test date first"
            : "";
          return (
            <span
              className={`li-dm-add-test-wrap${disabledHint ? " li-dm-tt" : ""}`}
              data-tt={disabledHint || undefined}
            >
              <button
                type="button"
                className="li-dm-add-test"
                onClick={openAnotherCard}
                disabled={!canAddAnother}
              >
                <PlusIcon /> Add another test
              </button>
            </span>
          );
        })()}

        {isDup ? (
          <p className="li-dm-err" role="alert">
            Another open card already covers this lab test + date. Change either
            to continue.
          </p>
        ) : null}
        {displayError ? (
          <p className="li-dm-err" role="alert">{displayError}</p>
        ) : null}
      </div>

      {/* Card footer — ghost + primary. Transcript mode (card's own
          `isStandalone` check returns false → no patientId/docVersion
          sentinel from LabInvestigationEditor) labels the ghost
          "Reject" per design doc §3; standalone mode keeps "Cancel". */}
      <div className="li-dm-card-foot">
        <button
          type="button"
          className="li-dm-btn li-dm-btn-ghost"
          onClick={() => {
            if (!isStandalone && !isBusy && typeof onReject === "function") {
              onReject();
              return;
            }
            onClose();
          }}
          disabled={isBusy}
        >
          {isStandalone ? "Cancel" : "Reject"}
        </button>
        <button
          type="button"
          className="li-dm-btn li-dm-btn-primary"
          onClick={commit}
          disabled={!canSubmit}
          title={submitHint}
        >
          {/* "Update" is reserved for the standalone edit flow — a real
              edit of an existing Lab v2 investigation row. In transcript
              mode `editing` is just the SEED shape handed by
              labCapturedItemsToCardEditing (so the AI-detected lab test /
              sub-test / LOINC pre-fills the picker) — the commit still
              goes through SaveSection as an add. Showing "Update" here
              misled the reviewer about what the button would do. */}
          {isBusy ? "Adding…" : (isUpdate && isStandalone ? "Update" : "Add")}
        </button>
      </div>
    </div>
  );
};

DigitiseManuallyCard.propTypes = {
  /** Unique identifier for aria bindings. */
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Optional pre-seeded card content: { labTest, testDate, subTests: [...] }.
   *  When absent, opens a blank card (add mode). */
  editing: PropTypes.shape({
    labTest:  PropTypes.object,
    testDate: PropTypes.string,
    subTests: PropTypes.array,
  }),
  /** Transcript-flow commit callback. Receives { labTest, testDate,
   *  subTests: [...] }. Not fired in standalone mode. */
  onAdd:        PropTypes.func,
  /** Optional "spawn a fresh empty card in the rail" callback for the
   *  "+ Add another test" affordance. */
  onAddAnother: PropTypes.func,
  onClose:      PropTypes.func.isRequired,
  /** Transcript-mode Reject handler — overrides onClose for the ghost button. */
  onReject:     PropTypes.func,
  saving:       PropTypes.bool,
  error:        PropTypes.string,
  themeClass:   PropTypes.string,
  blockedBySibling: PropTypes.bool,
  /** Sheet-stack blank-guard hook — fires `(formId, filled:boolean)`
   *  whenever the "has a parent lab test?" state flips. */
  onFilledChange: PropTypes.func,
  /** Standalone-mode props — when patientId + docVersion + docId are
   *  supplied, the card owns the LabInvestigationService.batch call
   *  itself and dispatches LI_V2_REFRESH on success. */
  patientId:     PropTypes.number,
  appointmentId: PropTypes.number,
  docId:         PropTypes.string,
  documents:     PropTypes.array,
  docToken:      PropTypes.string,
  docVersion:    PropTypes.number,
  onSaved:       PropTypes.func,
  /** Shortcut mode — an existing Investigation the card seeds parent
   *  lab test + first sub-test + unit from. Card self-fetches the doc
   *  list and renders Document ID as an editable dropdown. */
  shortcutFrom:  PropTypes.object,
};

DigitiseManuallyCard.defaultProps = {
  formId:       "",
  editing:      null,
  onAdd:        () => {},
  onAddAnother: null,
  saving:       false,
  error:        "",
  themeClass:   "",
  blockedBySibling: false,
  onFilledChange:   null,
  patientId:     0,
  appointmentId: 0,
  docId:         "",
  documents:     null,
  docToken:      "",
  docVersion:    undefined,
  onSaved:       null,
  shortcutFrom:  null,
};

export default DigitiseManuallyCard;
